import { baseApi, PaginatedResponse } from 'sama-shared';
import type {
  CreateDeliverySettlementAdminDto,
  DeliverySettlement,
  GetDeliverySettlementFilterAdminDto,
  UpdateDeliverySettlementAdminDto,
} from './types/delivery-settlement';

export const deliverySettlementApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['DeliverySettlement'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getDeliverySettlements: builder.query<
        PaginatedResponse<DeliverySettlement>,
        GetDeliverySettlementFilterAdminDto | void
      >({
        query: (params) => ({
          url: '/admin/delivery-settlement',
          method: 'GET',
          params: params || {},
        }),
        providesTags: (result) =>
          result?.data
            ? [
                ...result.data.map(({ id }) => ({ type: 'DeliverySettlement' as const, id })),
                { type: 'DeliverySettlement', id: 'LIST' },
              ]
            : [{ type: 'DeliverySettlement', id: 'LIST' }],
      }),

      getDeliverySettlementById: builder.query<DeliverySettlement, string>({
        query: (id) => ({
          url: `/admin/delivery-settlement/${id}`,
          method: 'GET',
        }),
        providesTags: (_result, _error, id) => [{ type: 'DeliverySettlement', id }],
      }),

      createDeliverySettlement: builder.mutation<
        DeliverySettlement,
        CreateDeliverySettlementAdminDto
      >({
        query: (newSettlement) => ({
          url: '/admin/delivery-settlement',
          method: 'POST',
          body: newSettlement,
        }),
        invalidatesTags: [{ type: 'DeliverySettlement', id: 'LIST' }],
      }),

      updateDeliverySettlement: builder.mutation<
        DeliverySettlement,
        { id: string; body: UpdateDeliverySettlementAdminDto }
      >({
        query: ({ id, body }) => ({
          url: `/admin/delivery-settlement/${id}`,
          method: 'PATCH',
          body,
        }),
        invalidatesTags: (_result, _error, { id }) => [
          { type: 'DeliverySettlement', id },
          { type: 'DeliverySettlement', id: 'LIST' },
          'AdminOrder',
        ],
      }),

      deleteDeliverySettlement: builder.mutation<void, string>({
        query: (id) => ({
          url: `/admin/delivery-settlement/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, _error, id) => [
          { type: 'DeliverySettlement', id },
          { type: 'DeliverySettlement', id: 'LIST' },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetDeliverySettlementsQuery,
  useGetDeliverySettlementByIdQuery,
  useCreateDeliverySettlementMutation,
  useUpdateDeliverySettlementMutation,
  useDeleteDeliverySettlementMutation,
} = deliverySettlementApi;
